import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { trackFeatureUsage, trackSearch, trackFormSubmission, trackEvent } from '../utils/analytics';

const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('stockNo');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Program sorting state
  const [programSortField, setProgramSortField] = useState('programName');
  const [programSortDirection, setProgramSortDirection] = useState('asc');
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    stockNo: '',
    productName: '',
    volumePoints: '',
    mrp: '',
    bronzePrice: '',
    silverPrice: '',
    goldPrice: '',
    successBuilderPrice: '',
    associatePrice: '',
    hsn: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  
  // Programs state
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [showAddProgramForm, setShowAddProgramForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [newProgram, setNewProgram] = useState({
    programName: '',
    programMrp: '',
    programCode: '',
    discountApplicable: true,
    sac: '',
    volumePoints: '',
    cashbackEligible: false,
    cashbackAmount: '',
    ctc: '',
    programType: 'CLUB',
    days: ''
  });
  const [programFormLoading, setProgramFormLoading] = useState(false);
  const [programFormError, setProgramFormError] = useState('');

  // Discount Plans state
  const [discountPlans, setDiscountPlans] = useState([]);
  const [discountPlansLoading, setDiscountPlansLoading] = useState(false);
  const [showAddDiscountForm, setShowAddDiscountForm] = useState(false);
  const [editingDiscountPlan, setEditingDiscountPlan] = useState(null);
  const [newDiscountPlan, setNewDiscountPlan] = useState({
    planName: '',
    discountPercentage: ''
  });
  const [discountFormLoading, setDiscountFormLoading] = useState(false);
  const [discountFormError, setDiscountFormError] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchPrograms();
    fetchDiscountPlans();
    
    // Track products page view on initial load
    if (currentPage === 0 && !searchQuery) {
      trackFeatureUsage('Products Management', 'view');
    }
  }, [currentPage, sortField, sortDirection, searchQuery]);

  const fetchProducts = async () => {
    try {
      setSearchLoading(true);
      
      const params = {
        page: currentPage,
        size: pageSize,
        search: searchQuery,
        sortBy: sortField,
        sortDir: sortDirection
      };
      
      const response = await axios.get('/api/products', { params });
      const data = response.data;
      
      setProducts(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      setProgramsLoading(true);
      const response = await axios.get('/api/products/programs');
      setPrograms(response.data);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setProgramsLoading(false);
    }
  };

  const fetchDiscountPlans = async () => {
    try {
      setDiscountPlansLoading(true);
      const response = await axios.get('/api/products/discount-plans');
      setDiscountPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch discount plans:', error);
    } finally {
      setDiscountPlansLoading(false);
    }
  };


  const handleAddProduct = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const productData = {
        ...newProduct,
        volumePoints: newProduct.volumePoints && newProduct.volumePoints !== '' ? parseFloat(newProduct.volumePoints) : null,
        mrp: newProduct.mrp && newProduct.mrp !== '' ? parseFloat(newProduct.mrp) : null,
        bronzePrice: newProduct.bronzePrice && newProduct.bronzePrice !== '' ? parseFloat(newProduct.bronzePrice) : null,
        silverPrice: newProduct.silverPrice && newProduct.silverPrice !== '' ? parseFloat(newProduct.silverPrice) : null,
        goldPrice: newProduct.goldPrice && newProduct.goldPrice !== '' ? parseFloat(newProduct.goldPrice) : null,
        successBuilderPrice: newProduct.successBuilderPrice && newProduct.successBuilderPrice !== '' ? parseFloat(newProduct.successBuilderPrice) : null,
        associatePrice: newProduct.associatePrice && newProduct.associatePrice !== '' ? parseFloat(newProduct.associatePrice) : null
      };

      // Debug logging
      console.log('Product data being sent:', productData);
      console.log('HSN value:', productData.hsn);

      if (editingProduct) {
        await axios.put(`/api/products/${editingProduct.id}`, productData);
        setConfirmMessage('Product updated successfully!');
        trackEvent('product_updated', {
          event_category: 'Product Management',
          event_label: productData.productName,
          product_id: editingProduct.id
        });
      } else {
        await axios.post('/api/products', productData);
        setConfirmMessage('Product added successfully!');
        trackEvent('product_added', {
          event_category: 'Product Management',
          event_label: productData.productName,
          mrp: productData.mrp
        });
      }

      trackFormSubmission(editingProduct ? 'edit_product_form' : 'add_product_form', true);
      setShowAddForm(false);
      setEditingProduct(null);
      setShowConfirmModal(true);
      resetForm();
      setCurrentPage(0);
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      setFormError(error.response?.data?.message || 'Failed to save product. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddProgram = async (e) => {
    e.preventDefault();
    setProgramFormLoading(true);
    setProgramFormError('');

    try {
      const programData = {
        ...newProgram,
        programMrp: newProgram.programMrp && newProgram.programMrp !== '' ? parseFloat(newProgram.programMrp) : null,
        volumePoints: newProgram.volumePoints && newProgram.volumePoints !== '' ? parseFloat(newProgram.volumePoints) : null,
        cashbackAmount: newProgram.cashbackAmount && newProgram.cashbackAmount !== '' ? parseFloat(newProgram.cashbackAmount) : null,
        ctc: newProgram.ctc && newProgram.ctc !== '' ? parseFloat(newProgram.ctc) : null,
        days: newProgram.days && newProgram.days !== '' ? parseInt(newProgram.days) : null
      };

      if (editingProgram) {
        await axios.put(`/api/products/programs/${editingProgram.id}`, programData);
        setConfirmMessage('Program updated successfully!');
      } else {
        await axios.post('/api/products/programs', programData);
        setConfirmMessage('Program added successfully!');
      }

      setShowAddProgramForm(false);
      setEditingProgram(null);
      setShowConfirmModal(true);
      resetProgramForm();
      fetchPrograms();
    } catch (error) {
      console.error('Failed to save program:', error);
      setProgramFormError(error.response?.data?.message || 'Failed to save program. Please try again.');
    } finally {
      setProgramFormLoading(false);
    }
  };

  const handleEditProgram = (program) => {
    setEditingProgram(program);
    setNewProgram({
      programName: program.programName || '',
      programMrp: program.programMrp || '',
      programCode: program.programCode || '',
      discountApplicable: program.discountApplicable !== undefined ? program.discountApplicable : true,
      sac: program.sac || '',
      volumePoints: program.volumePoints || '',
      cashbackEligible: program.cashbackEligible !== undefined ? program.cashbackEligible : false,
      cashbackAmount: program.cashbackAmount || '',
      ctc: program.ctc || '',
      programType: program.programType || 'CLUB',
      days: program.days || ''
    });
    setShowAddProgramForm(true);
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) {
      return;
    }

    try {
      await axios.delete(`/api/products/programs/${programId}`);
      setConfirmMessage('Program deleted successfully!');
      setShowConfirmModal(true);
      fetchPrograms();
    } catch (error) {
      console.error('Failed to delete program:', error);
      setError('Failed to delete program. Please try again.');
    }
  };

  const resetProgramForm = () => {
    setNewProgram({
      programName: '',
      programMrp: '',
      programCode: '',
      discountApplicable: true,
      sac: '',
      volumePoints: '',
      cashbackEligible: false,
      cashbackAmount: '',
      ctc: '',
      programType: 'CLUB',
      days: ''
    });
  };

  const handleAddDiscountPlan = async (e) => {
    e.preventDefault();
    setDiscountFormLoading(true);
    setDiscountFormError('');

    try {
      const planData = {
        ...newDiscountPlan,
        discountPercentage: newDiscountPlan.discountPercentage ? parseFloat(newDiscountPlan.discountPercentage) : null
      };

      if (editingDiscountPlan) {
        await axios.put(`/api/products/discount-plans/${editingDiscountPlan.id}`, planData);
        setConfirmMessage('Discount plan updated successfully!');
      } else {
        await axios.post('/api/products/discount-plans', planData);
        setConfirmMessage('Discount plan added successfully!');
      }

      setShowAddDiscountForm(false);
      setEditingDiscountPlan(null);
      setShowConfirmModal(true);
      resetDiscountForm();
      fetchDiscountPlans();
    } catch (error) {
      console.error('Failed to save discount plan:', error);
      setDiscountFormError(error.response?.data?.message || 'Failed to save discount plan. Please try again.');
    } finally {
      setDiscountFormLoading(false);
    }
  };

  const handleEditDiscountPlan = (plan) => {
    setEditingDiscountPlan(plan);
    setNewDiscountPlan({
      planName: plan.planName || '',
      discountPercentage: plan.discountPercentage || ''
    });
    setShowAddDiscountForm(true);
  };

  const handleDeleteDiscountPlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this discount plan?')) {
      return;
    }

    try {
      await axios.delete(`/api/products/discount-plans/${planId}`);
      setConfirmMessage('Discount plan deleted successfully!');
      setShowConfirmModal(true);
      fetchDiscountPlans();
    } catch (error) {
      console.error('Failed to delete discount plan:', error);
      setError('Failed to delete discount plan. Please try again.');
    }
  };

  const resetDiscountForm = () => {
    setNewDiscountPlan({
      planName: '',
      discountPercentage: ''
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setNewProduct({
      stockNo: product.stockNo || '',
      productName: product.productName || '',
      volumePoints: product.volumePoints || '',
      mrp: product.mrp || '',
      bronzePrice: product.bronzePrice || '',
      silverPrice: product.silverPrice || '',
      goldPrice: product.goldPrice || '',
      successBuilderPrice: product.successBuilderPrice || '',
      associatePrice: product.associatePrice || '',
      hsn: product.hsn || ''
    });
    setShowAddForm(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await axios.delete(`/api/products/${productId}`);
      setConfirmMessage('Product deleted successfully!');
      setShowConfirmModal(true);
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      setError('Failed to delete product. Please try again.');
    }
  };

  const resetForm = () => {
    setNewProduct({
      stockNo: '',
      productName: '',
      volumePoints: '',
      mrp: '',
      bronzePrice: '',
      silverPrice: '',
      goldPrice: '',
      successBuilderPrice: '',
      associatePrice: '',
      hsn: ''
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const handleSearch = (query) => {
    if (query && query.length >= 2) {
      trackSearch(query, 'products');
    }
    setSearchQuery(query);
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getProgramSortIcon = (field) => {
    if (programSortField !== field) return '↕️';
    return programSortDirection === 'asc' ? '↑' : '↓';
  };

  const handleProgramSort = (field) => {
    if (programSortField === field) {
      setProgramSortDirection(programSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setProgramSortField(field);
      setProgramSortDirection('asc');
    }
  };

  const sortedPrograms = [...programs].sort((a, b) => {
    const aValue = a[programSortField] || '';
    const bValue = b[programSortField] || '';
    
    if (programSortDirection === 'asc') {
      return aValue.toString().localeCompare(bValue.toString());
    } else {
      return bValue.toString().localeCompare(aValue.toString());
    }
  });

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Product Management</h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--gray-dark)', 
            marginTop: '5px'
          }}>
            Manage your product catalog
          </div>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setEditingProduct(null);
            resetForm();
            setShowAddForm(true);
          }}
          style={{ flexShrink: 0 }}
        >
          <span className="btn-icon">➕</span>
          <span className="btn-text">Add Product</span>
        </button>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            Products ({totalElements} total)
          </h3>
          <div style={{ position: 'relative', width: '300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ margin: 0, paddingRight: searchLoading ? '40px' : '35px' }}
            />
            {searchLoading && (
              <div style={{
                position: 'absolute',
                right: '35px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--primary-blue)'
              }}>
                ⏳
              </div>
            )}
            {searchQuery && (
              <button
                onClick={() => handleSearch('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Pagination Info */}
        {totalElements > 0 && (
          <div style={{ 
            marginBottom: '15px', 
            fontSize: '14px', 
            color: 'var(--gray-dark)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} products
              {searchQuery && ` (filtered by "${searchQuery}")`}
            </span>
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        )}

        {products.length === 0 ? (
          searchQuery ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No products found matching "{searchQuery}".
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No products found. Click "Add Product" to create your first product.
            </div>
          )
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('stockNo')}
                  >
                    Stock No {getSortIcon('stockNo')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('productName')}
                  >
                    Product Name {getSortIcon('productName')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('volumePoints')}
                  >
                    Volume Points {getSortIcon('volumePoints')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('mrp')}
                  >
                    MRP {getSortIcon('mrp')}
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Bronze
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Silver
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Gold
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Success Builder
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Supervisor
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    HSN
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr 
                    key={product.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {product.stockNo}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {product.productName}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {product.volumePoints || 'N/A'}
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      ₹{product.mrp}
                    </td>
                    <td style={{ padding: '12px' }}>
                      ₹{product.bronzePrice}
                    </td>
                    <td style={{ padding: '12px' }}>
                      ₹{product.silverPrice}
                    </td>
                    <td style={{ padding: '12px' }}>
                      ₹{product.goldPrice}
                    </td>
                    <td style={{ padding: '12px' }}>
                      ₹{product.successBuilderPrice}
                    </td>
                    <td style={{ padding: '12px' }}>
                      ₹{product.associatePrice}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {product.hsn || 'N/A'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleEditProduct(product)}
                          title="Edit Product"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                          onClick={() => handleDeleteProduct(product.id)}
                          title="Delete Product"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
            gap: '10px'
          }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(0)}
              disabled={currentPage === 0 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              First
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div style={{ display: 'flex', gap: '5px' }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i;
                } else if (currentPage < 3) {
                  pageNum = i;
                } else if (currentPage > totalPages - 4) {
                  pageNum = totalPages - 5 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`btn ${currentPage === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    style={{ 
                      fontSize: '12px', 
                      padding: '6px 10px',
                      minWidth: '35px'
                    }}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>
            
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Next
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={currentPage === totalPages - 1 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Programs Section */}
      <div className="card" style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            Programs ({programs.length} total)
          </h3>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingProgram(null);
              resetProgramForm();
              setShowAddProgramForm(true);
            }}
          >
            Add Program
          </button>
        </div>

        {programsLoading ? (
          <div className="loading">Loading programs...</div>
        ) : programs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No programs found. Click "Add Program" to create your first program.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleProgramSort('programName')}
                  >
                    Program Name {getProgramSortIcon('programName')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleProgramSort('programCode')}
                  >
                    Program Code {getProgramSortIcon('programCode')}
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    MRP
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Volume Points
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Discount Applicable
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Cashback Eligible
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Cashback Amount
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    CTC
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    SAC
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Type
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Days
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPrograms.map((program, index) => (
                  <tr 
                    key={program.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {program.programName}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {program.programCode}
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      ₹{program.programMrp}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {program.volumePoints || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: program.discountApplicable ? 'var(--success)' : 'var(--error)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {program.discountApplicable ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: program.cashbackEligible ? 'var(--success)' : 'var(--error)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {program.cashbackEligible ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {program.cashbackEligible && program.cashbackAmount ? `₹${program.cashbackAmount}` : 'N/A'}
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {program.ctc ? `₹${program.ctc}` : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {program.sac || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: program.programType === 'CLUB' ? 'var(--primary-blue)' : 'var(--success)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {program.programType || 'CLUB'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {program.days ? `${program.days} days` : 'N/A'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleEditProgram(program)}
                          title="Edit Program"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                          onClick={() => handleDeleteProgram(program.id)}
                          title="Delete Program"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Discount Plans Section */}
      <div className="card" style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            Program Discount Plans ({discountPlans.length} total)
          </h3>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingDiscountPlan(null);
              resetDiscountForm();
              setShowAddDiscountForm(true);
            }}
          >
            Add Discount Plan
          </button>
        </div>

        {discountPlansLoading ? (
          <div className="loading">Loading discount plans...</div>
        ) : discountPlans.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No discount plans found. Click "Add Discount Plan" to create your first plan.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Plan Name
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'left',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Discount Percentage
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {discountPlans.map((plan, index) => (
                  <tr 
                    key={plan.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {plan.planName}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {plan.discountPercentage}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleEditDiscountPlan(plan)}
                          title="Edit Discount Plan"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                          onClick={() => handleDeleteDiscountPlan(plan.id)}
                          title="Delete Discount Plan"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Product Form Modal */}
      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={handleAddProduct}>
              <div className="form-group">
                <label htmlFor="stockNo" className="form-label">
                  Stock No *
                </label>
                <input
                  type="text"
                  id="stockNo"
                  className="form-input"
                  value={newProduct.stockNo}
                  onChange={(e) => setNewProduct({...newProduct, stockNo: e.target.value})}
                  required
                  disabled={formLoading}
                  placeholder="e.g., STK001"
                />
              </div>

              <div className="form-group">
                <label htmlFor="productName" className="form-label">
                  Product Name *
                </label>
                <input
                  type="text"
                  id="productName"
                  className="form-input"
                  value={newProduct.productName}
                  onChange={(e) => setNewProduct({...newProduct, productName: e.target.value})}
                  required
                  disabled={formLoading}
                  placeholder="e.g., Protein Powder"
                />
              </div>

              <div className="form-group">
                <label htmlFor="volumePoints" className="form-label">
                  Volume Points
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="volumePoints"
                  className="form-input"
                  value={newProduct.volumePoints}
                  onChange={(e) => setNewProduct({...newProduct, volumePoints: e.target.value})}
                  disabled={formLoading}
                  placeholder="e.g., 25.50"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mrp" className="form-label">
                  MRP (₹) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="mrp"
                  className="form-input"
                  value={newProduct.mrp}
                  onChange={(e) => setNewProduct({...newProduct, mrp: e.target.value})}
                  required
                  disabled={formLoading}
                  placeholder="e.g., 2500.00"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="bronzePrice" className="form-label">
                    Bronze Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="bronzePrice"
                    className="form-input"
                    value={newProduct.bronzePrice}
                    onChange={(e) => setNewProduct({...newProduct, bronzePrice: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., 2250.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="silverPrice" className="form-label">
                    Silver Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="silverPrice"
                    className="form-input"
                    value={newProduct.silverPrice}
                    onChange={(e) => setNewProduct({...newProduct, silverPrice: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., 2000.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="goldPrice" className="form-label">
                    Gold Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="goldPrice"
                    className="form-input"
                    value={newProduct.goldPrice}
                    onChange={(e) => setNewProduct({...newProduct, goldPrice: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., 1750.00"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="successBuilderPrice" className="form-label">
                    Success Builder Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="successBuilderPrice"
                    className="form-input"
                    value={newProduct.successBuilderPrice}
                    onChange={(e) => setNewProduct({...newProduct, successBuilderPrice: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., 2125.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="associatePrice" className="form-label">
                    Associate Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="associatePrice"
                    className="form-input"
                    value={newProduct.associatePrice}
                    onChange={(e) => setNewProduct({...newProduct, associatePrice: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., 1875.00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="hsn" className="form-label">
                  HSN Code
                </label>
                <input
                  type="text"
                  id="hsn"
                  className="form-input"
                  value={newProduct.hsn}
                  onChange={(e) => setNewProduct({...newProduct, hsn: e.target.value})}
                  disabled={formLoading}
                  placeholder="e.g., 1234"
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                    setFormError('');
                    resetForm();
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? (editingProduct ? 'Updating...' : 'Adding...') : (editingProduct ? 'Update Product' : 'Add Product')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Program Form Modal */}
      {showAddProgramForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editingProgram ? 'Edit Program' : 'Add New Program'}
            </h3>

            {programFormError && (
              <div className="alert alert-error">{programFormError}</div>
            )}

            <form onSubmit={handleAddProgram}>
              <div className="form-group">
                <label htmlFor="programName" className="form-label">
                  Program Name *
                </label>
                <input
                  type="text"
                  id="programName"
                  className="form-input"
                  value={newProgram.programName}
                  onChange={(e) => setNewProgram({...newProgram, programName: e.target.value})}
                  required
                  disabled={programFormLoading}
                  placeholder="e.g., Basic 21 Days"
                />
              </div>

              <div className="form-group">
                <label htmlFor="programCode" className="form-label">
                  Program Code *
                </label>
                <input
                  type="text"
                  id="programCode"
                  className="form-input"
                  value={newProgram.programCode}
                  onChange={(e) => setNewProgram({...newProgram, programCode: e.target.value})}
                  required
                  disabled={programFormLoading}
                  placeholder="e.g., BASIC21"
                />
              </div>

              <div className="form-group">
                <label htmlFor="programMrp" className="form-label">
                  Program MRP (₹) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="programMrp"
                  className="form-input"
                  value={newProgram.programMrp}
                  onChange={(e) => setNewProgram({...newProgram, programMrp: e.target.value})}
                  required
                  disabled={programFormLoading}
                  placeholder="e.g., 5000.00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="discountApplicable" className="form-label">
                  Discount Applicable *
                </label>
                <select
                  id="discountApplicable"
                  className="form-input"
                  value={newProgram.discountApplicable}
                  onChange={(e) => setNewProgram({...newProgram, discountApplicable: e.target.value === 'true'})}
                  required
                  disabled={programFormLoading}
                >
                  <option value={true}>Yes - Customer discount slabs apply</option>
                  <option value={false}>No - Fixed price, no discounts</option>
                </select>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                  If "No" is selected, customer program discount slabs will be ignored for this program
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="volumePoints" className="form-label">
                  Volume Points
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="volumePoints"
                  className="form-input"
                  value={newProgram.volumePoints}
                  onChange={(e) => setNewProgram({...newProgram, volumePoints: e.target.value})}
                  disabled={programFormLoading}
                  placeholder="e.g., 25.50"
                />
              </div>

              <div className="form-group">
                <label htmlFor="cashbackEligible" className="form-label">
                  Cashback Eligible *
                </label>
                <select
                  id="cashbackEligible"
                  className="form-input"
                  value={newProgram.cashbackEligible}
                  onChange={(e) => setNewProgram({...newProgram, cashbackEligible: e.target.value === 'true', cashbackAmount: e.target.value === 'false' ? '' : newProgram.cashbackAmount})}
                  required
                  disabled={programFormLoading}
                >
                  <option value={false}>No - No cashback for this program</option>
                  <option value={true}>Yes - Cashback applicable</option>
                </select>
              </div>

              {newProgram.cashbackEligible && (
                <div className="form-group">
                  <label htmlFor="cashbackAmount" className="form-label">
                    Cashback Amount (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    id="cashbackAmount"
                    className="form-input"
                    value={newProgram.cashbackAmount}
                    onChange={(e) => setNewProgram({...newProgram, cashbackAmount: e.target.value})}
                    required={newProgram.cashbackEligible}
                    disabled={programFormLoading}
                    placeholder="e.g., 100.00"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="ctc" className="form-label">
                  CTC (Cost to Company) (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="ctc"
                  className="form-input"
                  value={newProgram.ctc}
                  onChange={(e) => setNewProgram({...newProgram, ctc: e.target.value})}
                  disabled={programFormLoading}
                  placeholder="e.g., 4500.00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="sac" className="form-label">
                  SAC Code
                </label>
                <input
                  type="text"
                  id="sac"
                  className="form-input"
                  value={newProgram.sac}
                  onChange={(e) => setNewProgram({...newProgram, sac: e.target.value})}
                  disabled={programFormLoading}
                  placeholder="e.g., 998314"
                />
              </div>

              <div className="form-group">
                <label htmlFor="programType" className="form-label">
                  Program Type *
                </label>
                <select
                  id="programType"
                  className="form-input"
                  value={newProgram.programType}
                  onChange={(e) => setNewProgram({...newProgram, programType: e.target.value})}
                  required
                  disabled={programFormLoading}
                >
                  <option value="CLUB">Club</option>
                  <option value="ONLINE">Online</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="days" className="form-label">
                  Days
                </label>
                <input
                  type="number"
                  min="1"
                  id="days"
                  className="form-input"
                  value={newProgram.days}
                  onChange={(e) => setNewProgram({...newProgram, days: e.target.value})}
                  disabled={programFormLoading}
                  placeholder="e.g., 21"
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddProgramForm(false);
                    setEditingProgram(null);
                    setProgramFormError('');
                    resetProgramForm();
                  }}
                  disabled={programFormLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={programFormLoading}
                >
                  {programFormLoading ? (editingProgram ? 'Updating...' : 'Adding...') : (editingProgram ? 'Update Program' : 'Add Program')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Discount Plan Form Modal */}
      {showAddDiscountForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editingDiscountPlan ? 'Edit Discount Plan' : 'Add New Discount Plan'}
            </h3>

            {discountFormError && (
              <div className="alert alert-error">{discountFormError}</div>
            )}

            <form onSubmit={handleAddDiscountPlan}>
              <div className="form-group">
                <label htmlFor="planName" className="form-label">
                  Plan Name *
                </label>
                <input
                  type="text"
                  id="planName"
                  className="form-input"
                  value={newDiscountPlan.planName}
                  onChange={(e) => setNewDiscountPlan({...newDiscountPlan, planName: e.target.value})}
                  required
                  disabled={discountFormLoading}
                  placeholder="e.g., Bronze"
                />
              </div>

              <div className="form-group">
                <label htmlFor="discountPercentage" className="form-label">
                  Discount Percentage (%) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  id="discountPercentage"
                  className="form-input"
                  value={newDiscountPlan.discountPercentage}
                  onChange={(e) => setNewDiscountPlan({...newDiscountPlan, discountPercentage: e.target.value})}
                  required
                  disabled={discountFormLoading}
                  placeholder="e.g., 10.00"
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddDiscountForm(false);
                    setEditingDiscountPlan(null);
                    setDiscountFormError('');
                    resetDiscountForm();
                  }}
                  disabled={discountFormLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={discountFormLoading}
                >
                  {discountFormLoading ? (editingDiscountPlan ? 'Updating...' : 'Adding...') : (editingDiscountPlan ? 'Update Plan' : 'Add Plan')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              Success!
            </h3>
            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {confirmMessage}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
